/**
 * @fileoverview Overall component for the Entity Selection launch step.
 *
 * @author Wyatt Lindquist (wlindquist@yext.com)
 */
import {Routes} from '/src/com/yext/listingslaunchstorm/js/routes';

import {useAjax} from '/src/com/yext/listingslaunchstorm/js/common/hooks/useajax';
import {ErrorCallout} from '/src/com/yext/listingslaunchstorm/js/enterprise/errorcallout/errorcallout';
import {EntitySelection} from '/src/com/yext/listingslaunchstorm/js/enterprise/selectentities/data/entityselection';
import {SavedFilterOption} from '/src/com/yext/listingslaunchstorm/js/enterprise/selectentities/data/savedfilteroption';
import {getSavedFilters} from '/src/com/yext/listingslaunchstorm/js/enterprise/selectentities/datasource/getsavedfilters';
import {NewSavedFilterModal} from '/src/com/yext/listingslaunchstorm/js/enterprise/selectentities/newsavedfiltermodal';
import {SelectSavedFilter} from '/src/com/yext/listingslaunchstorm/js/enterprise/selectentities/selectsavedfilter';
import {CalloutBanner, CalloutBannerType} from '/ui/components/calloutbanner/calloutbanner';
import {Card} from '/ui/components/card/card';
import {ContentSkeleton} from '/ui/components/contentskeleton/contentskeleton';

import * as styles from '/src/com/yext/listingslaunchstorm/js/enterprise/selectentities/selectentities.module.scss';
import FilterHopperSolidSvg from '/src/com/yext/listingslaunchstorm/public/icons/filter-hopper-solid.svg';

/**
 * @param {{
 *   selection: (EntitySelection | undefined),
 *   onChangeSelection: function(EntitySelection): void,
 * }} props
 * @returns {React.ReactNode}
 */
export function SelectEntities({
  selection,
  onChangeSelection,
}) {
  const {status, data, refetch} = useAjax(true, getSavedFilters);
  const [showModal, setShowModal] = React.useState(false);

  const onOpenModal = () => {
    setShowModal(true);
    window.open(Routes.entities(), '_blank');
  };
  const onCloseModal = () => {
    setShowModal(false);
    refetch();
  };

  if (status === 'error') {
    return (<ErrorCallout onReload={refetch} />);
  } else if (status !== 'success') {
    return (<ContentSkeleton type="box" height={100} />);
  } else {
    return (
      <div className={styles.page}>
        <NewSavedFilterModal show={showModal} onClose={onCloseModal} />
        <OverwriteWarningCallout />

        <Card taskFlowStyle>
          <Card.Body>
            <div className={styles.cardContainer}>
              <FilterHopperSolidSvg />
              <SelectSavedFilter
                options={data?.options || []}
                selected={selection?.savedFilterId}
                onSelect={option => onChangeSelection(entitySelectionFromSavedFilterOption(option))}
                onCreateNew={onOpenModal}
              />
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }
}

/**
 * @returns {React.ReactNode}
 */
function OverwriteWarningCallout() {
  const warning = yext.msg('Any decisions made in this Listings Launch flow will <b>overwrite</b> existing linked accounts and other publisher-specific settings for your selected entities.');

  return (
    <CalloutBanner type={CalloutBannerType.WARNING}>
      <span dangerouslySetInnerHTML={{__html: warning}} />
    </CalloutBanner>
  );
}

/**
 * @param {SavedFilterOption | undefined} option
 * @returns {EntitySelection}
 */
function entitySelectionFromSavedFilterOption(option) {
  return new EntitySelection({
    savedFilterId: option?.id || -1,
    entityCount: option?.entityCount || 0,
    displayName: option?.name || '',
  });
}
